// Report browser-side errors to the server (table client_errors, migration 0041).
//
// Why: a "something errors and input doesn't work" complaint left no trace —
// the failures happened in the browser before any request was sent, so the
// server logs were clean. Now render crashes, unhandled errors and every
// failed save (with the exact message the user saw) are recorded, and can be
// read under Audit Log → Error aplikasi.
//
// Fire-and-forget: reporting never throws, never blocks the UI, and is
// de-duplicated and rate-limited so one looping error cannot flood the table.

import { supabase } from '@/lib/supabase';

export type ClientErrorKind = 'render' | 'runtime' | 'promise' | 'mutation' | 'query';

const MAX_PER_SESSION = 50;
const DEDUPE_MS = 60_000;
const recent = new Map<string, number>();
let sent = 0;

/** The deployed bundle's file name — identifies which version a report came from. */
const appVersion = (() => {
  try {
    const src = [...document.scripts].map((s) => s.src).find((s) => /\/assets\/index-[^/]+\.js/.test(s));
    return src ? src.split('/').pop()!.slice(0, 100) : 'dev';
  } catch {
    return 'unknown';
  }
})();

const clip = (v: unknown, n: number) => (v == null ? null : String(v).slice(0, n));

function describe(err: unknown): { message: string; stack: string | null } {
  if (err instanceof Error) return { message: err.message || err.name, stack: err.stack ?? null };
  if (typeof err === 'string') return { message: err, stack: null };
  try {
    return { message: JSON.stringify(err), stack: null };
  } catch {
    return { message: String(err), stack: null };
  }
}

/** Noise that is not an app fault: navigation aborts, extensions, offline blips. */
function isNoise(message: string): boolean {
  return /ResizeObserver loop|AbortError|The user aborted|Load failed$|chrome-extension:|moz-extension:/i.test(message);
}

export function reportError(kind: ClientErrorKind, err: unknown, context?: Record<string, unknown>): void {
  try {
    const { message, stack } = describe(err);
    if (!message || isNoise(message)) return;
    const key = `${kind}|${message}|${location.pathname}`;
    const now = Date.now();
    if ((recent.get(key) ?? 0) > now - DEDUPE_MS) return;
    recent.set(key, now);
    if (sent >= MAX_PER_SESSION) return;
    sent += 1;

    void (async () => {
      // Only signed-in users can write; the login screen's own errors are not
      // worth an anonymous endpoint.
      const { data } = await supabase.auth.getSession();
      if (!data.session) return;
      await supabase.from('client_errors' as never).insert({
        kind,
        message: clip(message, 2000),
        stack: clip(stack, 8000),
        route: clip(location.pathname + location.search, 500),
        context: context ?? null,
        app_version: appVersion,
        user_agent: clip(navigator.userAgent, 500),
      } as never);
    })().catch(() => undefined);
  } catch {
    /* reporting must never break the app */
  }
}

/** Window-level handlers for errors nothing else caught. Call once at start-up. */
export function installGlobalErrorHandlers(): void {
  window.addEventListener('error', (e) => {
    reportError('runtime', e.error ?? e.message, {
      source: clip(e.filename, 300),
      line: e.lineno,
      col: e.colno,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    reportError('promise', e.reason);
  });
}
